import React, { ReactNode } from 'react'

import { FAQPageJsonLd } from 'next-seo'

import * as Accordion from '@radix-ui/react-accordion'
import * as Collapsible from '@radix-ui/react-collapsible'
import clsx from 'clsx'

import { isDevelopment } from '@/utils/isDevelopment'

import { Warning } from '../Warning'

type AccordionItem = {
	title: string
	children?: ReactNode
	answer?: string
}

type Props = {
	className?: string
	shownItems: number
	variant?: 'light' | 'dark'
	accordions: AccordionItem[]
	viewMore?: string
	viewLess?: string
	dataTestId?: string
}

function AccordionItem({
	value,
	title,
	children
}: {
	value: string
	title: string
	children: ReactNode
}) {
	return (
		<Accordion.Item
			value={value}
			className="group border-b border-gray-1000"
			data-testid="accordion-item"
		>
			<Accordion.Trigger className="flex w-full items-center gap-4 py-7">
				<h3 className="flex-1 text-left text-lg font-medium text-current">{title}</h3>

				<div className="w-3 border-[6px] border-b-0 border-x-transparent border-t-current transition-transform duration-[400ms] group-data-[state=open]:rotate-180"></div>
			</Accordion.Trigger>

			<Accordion.Content className="w-full pt-8 pb-14">
				<div>{children}</div>
			</Accordion.Content>
		</Accordion.Item>
	)
}

export function Accordions({
	className,
	accordions,
	shownItems,
	viewLess,
	viewMore,
	dataTestId = 'accordion',
	variant = 'light'
}: Props) {
	const [open, setOpen] = React.useState(false)

	if (accordions?.length === 0) {
		if (isDevelopment()) {
			return <Warning className={className}>There are no accordions</Warning>
		}

		return null
	}

	return (
		<>
			<FAQPageJsonLd
				mainEntity={accordions?.map((accordionItem, i) => ({
					questionName: accordionItem.title,
					acceptedAnswerText: accordionItem.answer
				}))}
			/>

			<div className={className} data-testid={dataTestId}>
				<Accordion.Root
					type="multiple"
					className={clsx({ light: 'text-black-100', dark: 'text-white' }[variant], 'w-full')}
				>
					{accordions?.slice(0, shownItems).map((accordionItem, i) => (
						<AccordionItem key={i} value={`${i}`} title={accordionItem.title}>
							{accordionItem.children}
						</AccordionItem>
					))}

					{accordions.length > shownItems ? (
						<Collapsible.Root open={open} onOpenChange={setOpen}>
							<Collapsible.Content>
								{accordions?.slice(shownItems).map((accordionItem, i) => (
									<AccordionItem key={i} value={`${i + shownItems}`} title={accordionItem.title}>
										{accordionItem.children}
									</AccordionItem>
								))}
							</Collapsible.Content>

							<Collapsible.Trigger asChild>
								<button className="mx-auto w-full cursor-pointer pt-14 pb-8 text uppercase text-current">
									{open ? viewLess || 'View less' : viewMore || 'View more'}
								</button>
							</Collapsible.Trigger>
						</Collapsible.Root>
					) : null}
				</Accordion.Root>
			</div>
		</>
	)
}
